/**
 * The AUTHOR's video library (F3-F): lists the course's `video_asset`s
 * (local-first), shows status (uploading/processing/ready/failed), allows
 * renaming (PATCH) and deleting (DELETE). Reusable as a selection grid
 * (`selectable`): only `status=ready` ones are eligible.
 */
import { type Component, createSignal, For, type JSX, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Check, Film, Loader2, Pencil, Trash2, TriangleAlert } from "@fleet/icons";
import type { VideoStatus } from "@space/shared";
import type { VideoAsset } from "@space/powersync";
import { useI18n } from "@/providers/i18n-provider";
import { showToast } from "@/components/ui/toast";
import { mediaApi, type MediaApi } from "../lib/media-api";
import { useCourseVideos } from "../hooks/use-media";

export interface VideoLibraryProps {
  courseId: string;
  api?: MediaApi;
  /** Selection mode: clicking a `ready` video invokes onSelect. */
  selectable?: boolean;
  onSelect?: (video: VideoAsset) => void;
  /** Currently selected id (highlights the card). */
  selectedId?: string;
}

const STATUS_META: Record<
  VideoStatus,
  { label: () => string; cls: string; icon: () => JSX.Element }
> = {
  uploading: {
    label: () => "uploading",
    cls: "bg-joy-sky/15 text-joy-sky",
    icon: () => <Loader2 class="h-3 w-3 motion-safe:animate-spin" />,
  },
  processing: {
    label: () => "processing",
    cls: "bg-joy-sun/20 text-foreground",
    icon: () => <Loader2 class="h-3 w-3 motion-safe:animate-spin" />,
  },
  ready: {
    label: () => "ready",
    cls: "bg-success/15 text-success",
    icon: () => <Check class="h-3 w-3" />,
  },
  failed: {
    label: () => "failed",
    cls: "bg-danger/15 text-danger",
    icon: () => <TriangleAlert class="h-3 w-3" />,
  },
};

function statusOf(v: VideoAsset): VideoStatus {
  const s = v.status;
  if (s === "uploading" || s === "processing" || s === "ready" || s === "failed") {
    return s;
  }
  return "processing";
}

export const VideoLibrary: Component<VideoLibraryProps> = (props) => {
  const { t } = useI18n();
  const api = () => props.api ?? mediaApi;
  const videos = useCourseVideos(() => props.courseId);
  const [editingId, setEditingId] = createSignal<string | null>(null);
  const [draftTitle, setDraftTitle] = createSignal("");

  const rename = async (video: VideoAsset) => {
    const title = draftTitle().trim();
    setEditingId(null);
    if (!title || title === video.title) {
      return;
    }
    try {
      await api().renameVideo(video.id, title);
      showToast(t(msg`Video renamed`), "success");
    } catch {
      showToast(t(msg`Could not rename the video`), "error");
    }
  };

  const remove = async (video: VideoAsset) => {
    try {
      await api().deleteVideo(video.id);
      showToast(t(msg`Video deleted`), "success");
    } catch {
      showToast(t(msg`Could not delete the video`), "error");
    }
  };

  return (
    <div data-testid="video-library">
      <Show
        when={(videos.data ?? []).length > 0}
        fallback={
          <div class="flex flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-border p-8 text-center text-muted-foreground">
            <Film class="h-8 w-8" />
            <p class="text-sm">{t(msg`No videos yet.`)}</p>
          </div>
        }
      >
        <ul class="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="video-library-grid">
          <For each={videos.data ?? []}>
            {(video) => {
              const status = () => statusOf(video);
              const ready = () => status() === "ready";
              const selected = () => props.selectedId === video.id;
              const meta = () => STATUS_META[status()];
              const pick = () => {
                if (props.selectable && ready()) {
                  props.onSelect?.(video);
                }
              };
              return (
                <li
                  class="group overflow-hidden rounded-xl border-2 transition-colors"
                  classList={{
                    "border-primary": selected(),
                    "border-border": !selected(),
                    "cursor-pointer hover:border-primary/50": props.selectable && ready(),
                    "opacity-60": props.selectable && !ready(),
                  }}
                  data-testid="video-library-item"
                  data-video-id={video.id}
                  data-video-status={status()}
                >
                  <button
                    type="button"
                    class="relative block aspect-video w-full bg-neutral-900"
                    data-testid="video-library-select"
                    disabled={props.selectable && !ready()}
                    onClick={pick}
                    aria-label={video.title ?? t(msg`Video`)}
                  >
                    <Show
                      when={ready()}
                      fallback={
                        <span class="flex h-full w-full items-center justify-center text-white/40">
                          <Film class="h-7 w-7" />
                        </span>
                      }
                    >
                      <img
                        src={api().posterUrl(video.id)}
                        alt=""
                        loading="lazy"
                        class="h-full w-full object-cover"
                      />
                    </Show>
                    <span
                      class={`absolute left-1.5 top-1.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${meta().cls}`}
                    >
                      {meta().icon()} {meta().label()}
                    </span>
                    <Show when={selected()}>
                      <span class="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <Check class="h-3.5 w-3.5" />
                      </span>
                    </Show>
                  </button>

                  <div class="flex items-center gap-1 p-2">
                    <Show
                      when={editingId() === video.id}
                      fallback={
                        <span
                          class="flex-1 truncate text-xs font-medium"
                          data-testid="video-library-title"
                        >
                          {video.title}
                        </span>
                      }
                    >
                      <input
                        class="min-w-0 flex-1 rounded border border-border bg-field-background px-1.5 py-0.5 text-xs"
                        data-testid="video-library-title-input"
                        value={draftTitle()}
                        autofocus
                        onInput={(e) => setDraftTitle(e.currentTarget.value)}
                        onBlur={() => void rename(video)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            void rename(video);
                          }
                        }}
                      />
                    </Show>
                    <Show when={!props.selectable}>
                      <button
                        type="button"
                        class="text-muted-foreground hover:text-foreground"
                        data-testid="video-library-rename"
                        aria-label={t(msg`Rename`)}
                        onClick={() => {
                          setDraftTitle(video.title ?? "");
                          setEditingId(video.id);
                        }}
                      >
                        <Pencil class="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        class="text-muted-foreground hover:text-danger"
                        data-testid="video-library-delete"
                        aria-label={t(msg`Delete`)}
                        onClick={() => void remove(video)}
                      >
                        <Trash2 class="h-3.5 w-3.5" />
                      </button>
                    </Show>
                  </div>
                </li>
              );
            }}
          </For>
        </ul>
      </Show>
    </div>
  );
};
